/** @jsx jsx */
import { jsx } from '@emotion/core';
import React from 'react';
import { useLocation } from 'react-router-dom';
import env from '../../../env';
import { usePost, useRouter, SiteLink } from '../../../components';
import { Loading, ErrorPopup } from '../../../components';
import { useAuth } from '../../../auth';
import { Signup } from '../../../auth';
import style from '../cart/cartStyle';
import { useStep } from '../cart/useStep';
import { StepNav } from '../cartStep/stepNav';
import { DartfishContact } from './dartfishContact';
import { Pay } from '../cartPayment/pay';

/**
 * Dedicated checkout for Dartfish products: Your Details (inc. Club) → Pay.
 * URL: /checkout/dartfish?productId=xxx
 * Uses CreateDartfishOrder (one-off order, one product). Requires login.
 */
export function DartfishCheckout() {
  const { query } = useRouter();
  const location = useLocation();
  const auth = useAuth();
  const productId = query.productId;
  const [cart, setCart] = React.useState(null);
  const [dartfishCartId, setDartfishCartId] = React.useState(null);
  const post = usePost();
  const hasCreatedRef = React.useRef(false);
  const [sendingStatus, setSendingStatus] = React.useState(null);
  const [goNext, setGoNext] = React.useState(false);
  const [authSettled, setAuthSettled] = React.useState(false);
  React.useEffect(() => {
    const t = setTimeout(() => setAuthSettled(true), 400);
    return () => clearTimeout(t);
  }, []);

  const results = post.response?.results ?? post.response?.Results;

  React.useEffect(() => {
    if (!productId || !auth.isResolved || !auth.user || hasCreatedRef.current) return;
    let cancelled = false;
    (async () => {
      try {
        const token = await auth.user.getIdToken();
        if (cancelled) return;
        hasCreatedRef.current = true;
        const url = env.apiBase + '/api/cart/CreateDartfishOrder';
        const data = { productId };
        post.sendRaw(
          (u, d) => ({
            url: u,
            options: {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
              body: JSON.stringify(d),
            },
          }),
          url,
          data
        );
      } catch (_) {
        if (!cancelled) hasCreatedRef.current = false;
      }
    })();
    return () => { cancelled = true; };
  }, [productId, auth.isResolved, auth.user]);

  React.useEffect(() => {
    if (post.status !== 'done' || !post.response) return;
    const res = post.response.results ?? post.response.Results;
    if (res?.cartModel && res?.cartInfo) {
      setCart(res.cartModel);
      const id = res.cartInfo?.cartId ?? res.cartInfo?.CartId;
      if (id) setDartfishCartId(id);
    }
  }, [post.status, post.response]);

  if (!productId) {
    return (
      <div css={style.cartContainer}>
        <div css={style.cart} id="ecmcart">
          <p>Missing product. Please start from the Dartfish product page.</p>
        </div>
      </div>
    );
  }

  if (authSettled && auth.isResolved && !auth.user && !cart) {
    const checkoutReturnUrl = location.pathname + (location.search || '');
    return (
      <div css={style.cartContainer}>
        <div css={style.cart} id="ecmcart">
          <Signup returnUrl={checkoutReturnUrl} />
        </div>
      </div>
    );
  }

  if (!auth.user && !cart) {
    return <Loading />;
  }

  const isAuthError = post.hasErrors() && post.errors.some(
    e => (e || '').toString().includes('401') ||
      (e || '').toString().toLowerCase().includes('log in') ||
      (e || '').toString().toLowerCase().includes('permission denied')
  );
  if (post.hasErrors()) {
    if (isAuthError) {
      const checkoutReturnUrl = location.pathname + (location.search || '');
      return (
        <div css={style.cartContainer}>
          <div css={style.cart} id="ecmcart">
            <Signup returnUrl={checkoutReturnUrl} />
          </div>
        </div>
      );
    }
    return <ErrorPopup title="Could not start checkout" errors={post.errors} />;
  }

  if (post.sending && post.sending()) {
    return <Loading />;
  }

  if (!cart || !dartfishCartId) {
    if (post.done() && !results?.cartModel) {
      return (
        <div css={style.cartContainer}>
          <div css={style.cart} id="ecmcart">
            <p>Could not create order. Please try again.</p>
          </div>
        </div>
      );
    }
    return <Loading />;
  }

  const totalQty = cart.cartInfo?.totalQuantity ?? cart.CartInfo?.TotalQuantity ?? 0;
  if (totalQty === 0) {
    return (
      <div css={style.cartContainer}>
        <div css={style.cart} id="ecmcart">
          <p>Could not create order. Please try again.</p>
        </div>
      </div>
    );
  }

  return (
    <DartfishCheckoutView
      cart={cart}
      setCart={setCart}
      dartfishCartId={dartfishCartId}
      post={post}
      sendingStatus={sendingStatus}
      setSendingStatus={setSendingStatus}
      goNext={goNext}
      setGoNext={setGoNext}
    />
  );
}

function DartfishCheckoutView({
  cart,
  setCart,
  dartfishCartId,
  post,
  sendingStatus,
  setSendingStatus,
  goNext,
  setGoNext,
}) {
  const stepInfo = useStep([
    { id: 'contact', title: 'Your Details' },
    { id: 'payment', title: 'Pay' },
  ]);
  const { steps, currentStep, lastStep, markAsFilled, moveNext } = stepInfo;

  function saveShipping(eOrPayload) {
    const value =
      typeof eOrPayload === 'object' &&
      eOrPayload !== null &&
      !eOrPayload.preventDefault
        ? { ...eOrPayload, cartId: eOrPayload.cartId ?? dartfishCartId }
        : { cartId: dartfishCartId };
    post.send(env.apiBase + '/api/cart/saveShippingInfo', value);
    setSendingStatus('pending');
    setGoNext(true);
  }

  React.useEffect(() => {
    if (post.done() && sendingStatus === 'pending') {
      const newCart = post.response?.results?.cartModel;
      setSendingStatus(null);
      if (newCart) setCart(newCart);
      if (goNext) {
        markAsFilled(currentStep);
        moveNext();
      }
    }
  }, [post.status, sendingStatus, goNext, currentStep]);

  const stepStyle = (index) => {
    const locStyle =
      index === currentStep
        ? style.stepCurrent
        : index === currentStep - 1
        ? style.stepPrev
        : index === currentStep + 1
        ? style.stepNext
        : style.step;
    const movingStyle = index === lastStep ? style.stepLast : null;
    return [locStyle, movingStyle];
  };

  return (
    <div css={style.cartContainer}>
      <div css={style.cart} id="ecmcart">
        <div css={style.stepsWrapper}>
          <StepNav stepInfo={stepInfo} />
          <div css={style.steps} style={{ transform: `translateX(${-100 * currentStep}%)` }}>
            {steps.map((step, index) => {
              let stepView = null;
              switch (step.id) {
                case 'contact':
                  stepView = (
                    <DartfishContact
                      cart={cart}
                      saveShipping={saveShipping}
                      post={post}
                      cartId={dartfishCartId}
                    />
                  );
                  break;
                case 'payment':
                  stepView = (
                    <>
                      <div className="alert alert-info" role="alert" style={{ marginBottom: '1rem' }}>
                        Once payment is complete, the Dartfish team will be in touch with you.
                      </div>
                      <Pay cart={cart} post={post} cartId={dartfishCartId} />
                    </>
                  );
                  break;
                default:
                  break;
              }
              return (
                <div css={stepStyle(index)} key={step.id}>
                  {stepView}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
